// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Divider, MultiSelect, Select, Text } from '@mantine/core';
import { IconAt, IconMail } from '@tabler/icons';
import {
  FC,
  KeyboardEvent as ReactKeyboardEvent,
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeText } from '../../../Atoms/ThemeText';
import { useAddon, useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { ProfileShare } from '../../../Service/ProfileS';
import { MailOutBoundConfigDTO } from '../../../Types/MailT';
import SignatureEditor from '../Emails/SignatureEditor';
import { openTeamAddonMissingModal } from './bookitupTeamAddonModals';
import { areMailAccountsEqual, normalizeMailAccounts } from './bookitupTeamUtils';

const TeamEmailSettings: FC<{
  share: ProfileShare;
  mailOutboundConfigs: MailOutBoundConfigDTO[];
  loadingMailAccounts: boolean;
}> = ({ share, mailOutboundConfigs, loadingMailAccounts }) => {
  const { bookitupTeam } = useContext(ProfileContext);
  const { patchShareSettings } = bookitupTeam;
  const { isPresent: isTeamAddonPresent } = useAddon('team');
  const { subscription } = useBookitupSubscription();
  const navigate = useNavigate();
  const lastAddonPromptAt = useRef(0);
  const [signature, setSignature] = useState('');
  const [availableMailAccounts, setAvailableMailAccounts] = useState<number[]>([]);
  const [defaultAccount, setDefaultAccount] = useState<number | null>(null);
  const mailAccountIds = useMemo(
    () => normalizeMailAccounts(mailOutboundConfigs.filter((c) => c.connectionVerified).map((config) => config.id)),
    [mailOutboundConfigs],
  );
  const mailAccountOptions = useMemo(
    () =>
      mailOutboundConfigs
        .filter((config) => config.connectionVerified)
        .map((config) => ({ value: String(config.id), label: config.email })),
    [mailOutboundConfigs],
  );
  const profileDefaultAccount = mailOutboundConfigs.find((config) => config.defaultAccount)?.id || null;

  useEffect(() => {
    const incomingMailAccounts = normalizeMailAccounts(share.availableMailAccounts).filter((account) =>
      mailAccountIds.includes(account),
    );
    const fallbackDefaultAccount =
      profileDefaultAccount !== null && incomingMailAccounts.includes(profileDefaultAccount)
        ? profileDefaultAccount
        : null;
    const incomingDefaultAccount = share.defaultAccount ?? fallbackDefaultAccount;

    setSignature(share.signature || '');
    setAvailableMailAccounts(incomingMailAccounts);
    setDefaultAccount(
      incomingDefaultAccount !== null && incomingMailAccounts.includes(incomingDefaultAccount)
        ? incomingDefaultAccount
        : null,
    );
  }, [mailAccountIds, profileDefaultAccount, share]);

  const savedMailAccounts = normalizeMailAccounts(share.availableMailAccounts);
  const hasInvalidDefaultAccount = defaultAccount !== null && !availableMailAccounts.includes(defaultAccount);
  let mailAccountsPlaceholder = 'Keine Absenderkonten konfiguriert';

  if (loadingMailAccounts) {
    mailAccountsPlaceholder = 'Absenderkonten werden geladen';
  } else if (mailAccountOptions.length > 0) {
    mailAccountsPlaceholder = 'Absenderkonten auswählen';
  }

  const requestTeamAddon = () => {
    const now = Date.now();
    if (now - lastAddonPromptAt.current < 500) {
      return;
    }

    lastAddonPromptAt.current = now;
    openTeamAddonMissingModal(navigate, subscription?.interval);
  };

  const guardTeamAddonInteraction = (event: SyntheticEvent) => {
    if (isTeamAddonPresent) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    requestTeamAddon();
  };

  const guardTeamAddonKeyboardInteraction = (event: ReactKeyboardEvent) => {
    if (['Tab', 'Shift', 'Escape'].includes(event.key)) {
      return;
    }

    guardTeamAddonInteraction(event);
  };

  const patchMailAccounts = async (nextAvailableMailAccounts: number[], nextSelectedDefaultAccount: number | null) => {
    if (loadingMailAccounts) {
      return;
    }

    const nextMailAccounts = normalizeMailAccounts(nextAvailableMailAccounts).filter((account) =>
      mailAccountIds.includes(account),
    );
    const nextDefaultAccount =
      nextSelectedDefaultAccount !== null && nextMailAccounts.includes(nextSelectedDefaultAccount)
        ? nextSelectedDefaultAccount
        : null;

    if (
      areMailAccountsEqual(nextMailAccounts, savedMailAccounts) &&
      nextDefaultAccount === (share.defaultAccount ?? null)
    ) {
      return;
    }

    if (!isTeamAddonPresent) {
      requestTeamAddon();
      return;
    }

    await patchShareSettings(share.applicationUserId, {
      availableMailAccounts: nextMailAccounts,
      defaultAccount: nextDefaultAccount,
    });
  };

  const updateMailAccounts = async (accounts: string[]) => {
    if (!isTeamAddonPresent) {
      requestTeamAddon();
      return;
    }

    const normalizedAccounts = normalizeMailAccounts(accounts);
    const nextDefaultAccount =
      defaultAccount !== null && !normalizedAccounts.includes(defaultAccount)
        ? normalizedAccounts[0] ?? null
        : defaultAccount;

    setAvailableMailAccounts(normalizedAccounts);
    setDefaultAccount(nextDefaultAccount);

    await patchMailAccounts(normalizedAccounts, nextDefaultAccount);
  };

  const patchDefaultAccount = async () => {
    if (loadingMailAccounts || hasInvalidDefaultAccount || defaultAccount === (share.defaultAccount ?? null)) {
      return;
    }

    if (!isTeamAddonPresent) {
      requestTeamAddon();
      return;
    }

    await patchShareSettings(share.applicationUserId, {
      defaultAccount,
    });
  };

  const updateDefaultAccount = (value: string | null) => {
    if (!isTeamAddonPresent) {
      requestTeamAddon();
      return;
    }

    setDefaultAccount(value !== null ? Number(value) : null);
  };

  const patchSignature = async (nextSignature: string) => {
    if (nextSignature === (share.signature || '')) {
      return;
    }

    if (!isTeamAddonPresent) {
      requestTeamAddon();
      return;
    }

    setSignature(nextSignature);

    await patchShareSettings(share.applicationUserId, {
      signature: nextSignature,
    });
  };

  return (
    <>
      <ThemeText size="sm" mb="xl">
        Wähle aus, welche Absenderkonten dieses Teammitglied verwenden darf und welche Signatur beim Schreiben von
        E-Mails genutzt wird.
      </ThemeText>

      <Box
        onPointerDownCapture={guardTeamAddonInteraction}
        onClickCapture={guardTeamAddonInteraction}
        onKeyDownCapture={guardTeamAddonKeyboardInteraction}
      >
        <MultiSelect
          data={mailAccountOptions}
          value={availableMailAccounts.map(String)}
          label="Verfügbare Absenderkonten"
          placeholder={mailAccountsPlaceholder}
          searchable
          clearable
          disabled={loadingMailAccounts || mailAccountOptions.length === 0}
          icon={<IconAt size={16} />}
          onChange={updateMailAccounts}
        />
      </Box>

      <Box
        onPointerDownCapture={guardTeamAddonInteraction}
        onClickCapture={guardTeamAddonInteraction}
        onKeyDownCapture={guardTeamAddonKeyboardInteraction}
      >
        <Select
          my="md"
          data={mailAccountOptions.filter((option) => availableMailAccounts.includes(Number(option.value)))}
          value={defaultAccount !== null ? String(defaultAccount) : null}
          label="Standardkonto"
          placeholder={availableMailAccounts.length > 0 ? 'Standardkonto auswählen' : 'Keine Absenderkonten hinterlegt'}
          clearable
          disabled={loadingMailAccounts || availableMailAccounts.length === 0}
          icon={<IconMail size={16} />}
          onChange={updateDefaultAccount}
          onBlur={patchDefaultAccount}
          error={
            hasInvalidDefaultAccount
              ? 'Das Standardkonto muss in den verfügbaren Absenderkonten enthalten sein.'
              : undefined
          }
        />
      </Box>

      <Divider my="md" label={<ThemeText>E-Mail-Signatur</ThemeText>} labelPosition="center" />
      <Box
        onPointerDownCapture={guardTeamAddonInteraction}
        onClickCapture={guardTeamAddonInteraction}
        onKeyDownCapture={guardTeamAddonKeyboardInteraction}
      >
        <SignatureEditor signature={signature} onChange={patchSignature} allowPlaceholders={false} />
      </Box>
      <Text size="xs" color="dimmed" mt={-5} align="center">
        Die Signatur kann auch direkt vom Teammitglied in den eigenen Einstellungen aktualisiert werden
      </Text>
    </>
  );
};

export default TeamEmailSettings;
